import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { DateDisplay } from '@/components/date-display';
import { useFormatDate } from '@/hooks/use-format-date';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ArrowLeft, Check, Clock, Eye, EyeOff, Info, Loader2, Lock, RefreshCw, ShoppingBag } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePluginInstallation } from './installation/PluginInstallationContext';
import { PluginPreviewViewer } from './plugin-preview-viewer';
import { PluginDeployment, PluginFeature } from './types';

/**
 * Convert technical page path to user-friendly display name
 */
function getPageDisplayName(path: string, name?: string): string {
  // If name is provided, use it
  if (name) return name;

  // Remove leading/trailing slashes and split
  const cleanPath = path.replace(/^\/|\/$/g, '').toLowerCase();

  // Handle common patterns
  const pathMappings: Record<string, string> = {
    checkout: 'Checkout Page',
    'thank-you': 'Thank You Page',
    thankyou: 'Thank You Page',
    'order-confirmation': 'Order Confirmation',
    upsell: 'Order Bump Offer',
    downsell: 'Downsell Offer',
    offer: 'Special Offer',
    landing: 'Landing Page',
    home: 'Home Page',
    index: 'Home Page',
    '': 'Home Page',
    product: 'Product Page',
    cart: 'Shopping Cart',
    account: 'Account Page',
    login: 'Login Page',
    signup: 'Sign Up Page',
    error: 'Error Page',
    '404': 'Error Page',
  };

  // Check for dynamic routes (e.g., /post/:orderId, /product/:id)
  if (cleanPath.includes(':')) {
    const basePath = cleanPath.split(':')[0].replace(/\/$/, '');
    const dynamicPart = cleanPath.split(':')[1];

    // Handle common dynamic routes
    if (basePath.includes('post') || basePath.includes('order')) {
      return 'Order Details';
    }
    if (basePath.includes('product')) {
      return 'Product Details';
    }
    if (basePath.includes('page')) {
      return 'Content Page';
    }
    if (basePath) {
      const formattedBase = basePath.split('/').pop() || '';
      return formattedBase.charAt(0).toUpperCase() + formattedBase.slice(1).replace(/-/g, ' ') + ' Details';
    }
    return 'Dynamic Page';
  }

  // Check direct mapping
  const firstSegment = cleanPath.split('/')[0] || '';
  if (pathMappings[firstSegment]) {
    return pathMappings[firstSegment];
  }

  // Default: capitalize and format the path
  if (cleanPath) {
    return cleanPath
      .split('/')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '))
      .join(' - ');
  }

  return 'Page';
}

interface PluginDetailsProps {
  pluginId: string;
  storeId: string;
  viewMode: 'mine' | 'public';
  onInstall: (deploymentId?: string, selectedPreset?: string) => void;
  onToggleVisibility?: (
    deploymentId: string,
    currentPublic: boolean,
    currentRequested: boolean,
    event: React.MouseEvent,
  ) => void;
  onManagePreviews?: (deployment: PluginDeployment, pluginName: string, event: React.MouseEvent) => void;
  /** When true, shows install button but clicking it triggers onInstall (for signup flow) */
  readOnly?: boolean;
  /** Optional back button handler for nested layout mode */
  onBack?: () => void;
}

interface PluginDetailsDialogProps extends PluginDetailsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Plugin details content (can be used standalone or in a dialog)
 * Split view: Viewer (3/4) on left, Details (1/4) on right
 */
export function PluginDetails({
  pluginId,
  storeId,
  viewMode,
  onInstall,
  onToggleVisibility,
  onManagePreviews: _onManagePreviews,
  readOnly = false,
  onBack,
}: PluginDetailsProps) {
  const { t } = useTranslation('storefront');
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const installation = usePluginInstallation();
  const { format: formatTz } = useFormatDate();
  const [selectedDeployment, setSelectedDeployment] = useState<PluginDeployment | null>(null);
  const [previewViewMode, setPreviewViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [selectedPreset, setSelectedPreset] = useState<string>('default');
  const [isInstalling, setIsInstalling] = useState(false);
  const [showAllVersions, setShowAllVersions] = useState(false);

  // Determine if we should use public endpoint
  const usePublicEndpoint = readOnly || viewMode === 'public';

  // Public endpoint (no auth required)
  const {
    data: publicPlugin,
    isLoading: isLoadingPublic,
    refetch: refetchPublic,
    isFetching: isFetchingPublic,
  } = trpc.plugins.getPublicDetails.useQuery(
    {
      pluginId: pluginId || '',
    },
    {
      enabled: usePublicEndpoint && !!pluginId,
      refetchOnWindowFocus: false,
    },
  );

  // Protected endpoint (auth required)
  const {
    data: protectedPlugin,
    isLoading: isLoadingProtected,
    refetch: refetchProtected,
    isFetching: isFetchingProtected,
  } = trpc.plugins.getDetails.useQuery(
    {
      pluginId: pluginId || '',
      storeId,
    },
    {
      enabled: !usePublicEndpoint && !!pluginId,
      refetchOnWindowFocus: false,
    },
  );

  // Use the appropriate data source
  const plugin = usePublicEndpoint ? publicPlugin : protectedPlugin;
  const isLoading = usePublicEndpoint ? isLoadingPublic : isLoadingProtected;
  const isFetching = usePublicEndpoint ? isFetchingPublic : isFetchingProtected;
  const refetch = usePublicEndpoint ? refetchPublic : refetchProtected;

  // Sort deployments by version
  const sortedDeployments = plugin?.deployments
    ? [...plugin.deployments].sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true }))
    : [];

  const latestDeployment = sortedDeployments[0];

  // Debug: Log deployment data to check if pages and configPresets are present
  if (latestDeployment) {
    console.log('[PLUGIN DETAILS] Latest deployment:', {
      deploymentId: latestDeployment.deploymentId,
      version: latestDeployment.version,
      hasPages: !!latestDeployment.pages,
      pagesCount: latestDeployment.pages?.length,
      hasConfigPresets: !!latestDeployment.configPresets,
      configPresetsKeys: latestDeployment.configPresets ? Object.keys(latestDeployment.configPresets) : [],
    });
  }

  // Reset and set to latest deployment when plugin changes
  // Also update when deployment data changes (e.g., public status changes)
  useEffect(() => {
    if (latestDeployment) {
      setSelectedDeployment(latestDeployment);
      // Set to 'default' preset if it exists, otherwise use the first one
      const presetKeys = latestDeployment.configPresets ? Object.keys(latestDeployment.configPresets) : [];
      const defaultPreset = presetKeys.includes('default')
        ? 'default'
        : presetKeys.length > 0
          ? presetKeys[0]
          : 'default';
      setSelectedPreset(defaultPreset);
    }
  }, [latestDeployment, plugin?.id, plugin?.deployments]);

  // Set preset when configPresets become available for selected deployment
  // Only update if current preset is invalid (not in the list)
  useEffect(() => {
    if (selectedDeployment?.configPresets) {
      const presetKeys = Object.keys(selectedDeployment.configPresets);
      if (presetKeys.length > 0) {
        // Only update if current preset is not in the list (invalid preset)
        if (!presetKeys.includes(selectedPreset)) {
          // Prefer 'default' if it exists, otherwise use the first preset
          const targetPreset = presetKeys.includes('default') ? 'default' : presetKeys[0];
          setSelectedPreset(targetPreset);
        }
      }
    }
  }, [selectedDeployment?.configPresets]);

  // Reset installing state when wizard or config editor opens
  useEffect(() => {
    if (installation.wizardOpen || installation.configEditorOpen) {
      setIsInstalling(false);
    }
  }, [installation.wizardOpen, installation.configEditorOpen]);

  const uniqueStoreCount = plugin?.uniqueStoreCount || 0;

  // Handle refresh versions - force refetch from server (optimized - only fetches this plugin)
  const handleRefreshVersions = async () => {
    try {
      // First invalidate the cache to force a fresh fetch
      await utils.plugins.getDetails.invalidate({ pluginId: pluginId || '', storeId });
      // Then refetch to get the latest data
      await refetch();
      toast({
        title: t('plugins.details.refreshed'),
        description: t('plugins.details.versionsUpdated'),
      });
    } catch (error) {
      toast({
        title: t('plugins.details.error'),
        description: t('plugins.details.failedRefreshVersions'),
        variant: 'destructive',
      });
    }
  };

  // Handle install click
  const handleInstallClick = () => {
    setIsInstalling(true);
    onInstall(displayDeployment?.deploymentId, selectedPreset);
  };

  // Use pre-computed features from backend - normalize to strings
  const featuresArray = (plugin?.allFeatures ?? [])
    .map((f: string | PluginFeature) => {
      if (typeof f === 'string') return f;
      if (f && typeof f === 'object' && 'type' in f) {
        const feature = f as PluginFeature;
        return typeof feature.type === 'string' ? feature.type : null;
      }
      return null;
    })
    .filter((f): f is string => f !== null && typeof f === 'string');

  // Current deployment to display in viewer (default to latest)
  const displayDeployment = selectedDeployment || latestDeployment;

  // Show loading state (maintain same layout structure for consistent sizing)
  if (isLoading || !plugin) {
    return (
      <div className="flex h-full max-h-full flex-col overflow-hidden">
        {/* Back button placeholder (if provided) */}
        {onBack && (
          <div className="bg-background shrink-0 border-b px-4 py-1.5">
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t('plugins.details.backToMarketplace')}
            </Button>
          </div>
        )}

        <div className="flex flex-1 flex-row gap-0 overflow-hidden">
          {/* LEFT: Preview area placeholder (3/4 width) */}
          <div className="bg-muted/20 flex w-3/4 flex-col items-center justify-center overflow-hidden border-r">
            <RefreshCw className="text-primary h-8 w-8 animate-spin" />
            <p className="text-muted-foreground mt-4 text-sm">{t('plugins.details.loadingDetails')}</p>
          </div>

          {/* RIGHT: Details sidebar placeholder (1/4 width) */}
          <div className="bg-background flex w-1/4 flex-col overflow-hidden">
            {/* Header placeholder */}
            <div className="shrink-0 border-b px-4 py-2">
              <div className="bg-muted h-5 w-32 animate-pulse rounded" />
              <div className="bg-muted mt-1.5 h-3 w-24 animate-pulse rounded" />
            </div>

            {/* Content placeholder */}
            <div className="flex-1 space-y-3 overflow-y-auto overflow-x-hidden p-3">
              <div className="space-y-2">
                <div className="bg-muted h-4 w-full animate-pulse rounded" />
                <div className="bg-muted h-4 w-3/4 animate-pulse rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full max-h-full flex-col overflow-hidden">
      {/* Back button (if provided) */}
      {onBack && (
        <div className="bg-background shrink-0 border-b px-4 py-1.5">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('plugins.details.backToMarketplace')}
          </Button>
        </div>
      )}

      <div className="flex flex-1 flex-row gap-0 overflow-hidden border-t">
        {/* LEFT: Preview Viewer (3/4 width) */}
        <div className="bg-muted/10 flex w-3/4 flex-col overflow-hidden border-r">
          {displayDeployment?.deploymentId ? (
            <div className="flex flex-1 flex-col overflow-hidden">
              {/* Active Preset Indicator */}
              {selectedPreset !== 'default' && (
                <div className="bg-primary/5 border-b px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-primary/20 rounded-full p-1.5">
                      <div className="bg-primary h-2 w-2 animate-pulse rounded-full" />
                    </div>
                    <p className="text-sm font-medium">
                      {t('plugins.details.previewingWithStyle', { style: selectedPreset })}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-auto">
                <PluginPreviewViewer
                  key={displayDeployment.deploymentId}
                  deploymentId={displayDeployment.deploymentId}
                  editable={viewMode === 'mine'}
                  viewMode={previewViewMode}
                  onViewModeChange={setPreviewViewMode}
                  selectedPreset={selectedPreset}
                  deploymentPages={displayDeployment.pages || []}
                />
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground text-sm">{t('plugins.details.noVersionSelected')}</p>
            </div>
          )}
        </div>

        {/* RIGHT: Details Sidebar (1/4 width) */}
        <div className="flex w-1/4 flex-col overflow-hidden bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
          {/* Header */}
          <div className="shrink-0 border-b bg-white/90 px-5 py-5 backdrop-blur-sm dark:bg-slate-950/90">
            <h2 className="text-2xl font-bold leading-tight">{plugin.name}</h2>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 space-y-5 overflow-y-auto overflow-x-hidden p-5">
            {/* Stats */}
            {uniqueStoreCount > 0 && (
              <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/30">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-950">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold leading-none text-green-900 dark:text-green-100">
                    {t('plugins.details.activeInstallations', { count: uniqueStoreCount })}
                  </p>
                  <p className="text-muted-foreground mt-1.5 text-xs">
                    {t('plugins.details.trustedByStores', { count: uniqueStoreCount })}
                  </p>
                </div>
              </div>
            )}

            {/* Features */}
            {featuresArray.length > 0 && (
              <div>
                <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  {t('plugins.details.featuresIncluded')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {featuresArray.map((feature, idx) => (
                    <Badge
                      key={`${feature}-${idx}`}
                      variant="secondary"
                      className="bg-primary/10 text-primary border-primary/20 border px-3 py-1.5 text-xs font-semibold"
                    >
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Version (Prominent) */}
            {selectedDeployment && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    {t('plugins.details.selectedVersion')}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefreshVersions}
                    disabled={isLoading || isFetching}
                    className="h-8 w-8 p-0"
                    title="Refresh versions"
                  >
                    <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                {(() => {
                  const isLatest = selectedDeployment.deploymentId === latestDeployment?.deploymentId;
                  const isInstalled = (selectedDeployment.instances?.length ?? 0) > 0;
                  const instanceCount = selectedDeployment.instances?.length ?? 0;

                  return (
                    <div className="border-primary from-primary/5 to-primary/10 overflow-hidden rounded-xl border-2 bg-gradient-to-br shadow-md transition-all">
                      {/* Version Header */}
                      <div className="w-full rounded-t-xl bg-white/90 p-4 text-left backdrop-blur-sm dark:bg-slate-900/90">
                        <div className="mb-2 flex items-center gap-2">
                          <code className="font-mono text-lg font-bold">v{selectedDeployment.version}</code>
                          {isLatest && (
                            <Badge
                              variant="default"
                              className="h-5 bg-gradient-to-r from-emerald-600 to-teal-600 text-[10px] font-bold"
                            >
                              {t('plugins.details.latest')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {isInstalled && (
                            <Badge
                              variant="outline"
                              className="h-5 border-emerald-300 bg-emerald-50 text-[10px] font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-400"
                            >
                              <Check className="mr-1 h-2.5 w-2.5" />
                              {t('plugins.details.installed')}
                              {instanceCount > 1 && ` (${instanceCount}×)`}
                            </Badge>
                          )}
                          {selectedDeployment.public ? (
                            <Badge
                              variant="outline"
                              className="h-5 border-green-300 bg-green-50 text-[10px] font-semibold text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
                            >
                              <Eye className="mr-1 h-2.5 w-2.5" />
                              {t('plugins.details.public')}
                            </Badge>
                          ) : selectedDeployment.requestedForPublic ? (
                            <Badge
                              variant="secondary"
                              className="h-5 border border-amber-300 bg-amber-50 text-[10px] font-semibold text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400"
                            >
                              <Clock className="mr-1 h-2.5 w-2.5" />
                              {t('plugins.details.pendingReview')}
                            </Badge>
                          ) : (
                            <Badge
                              variant="secondary"
                              className="h-5 border border-blue-300 bg-blue-50 text-[10px] font-semibold text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-400"
                            >
                              <Lock className="mr-1 h-2.5 w-2.5" />
                              {t('plugins.details.private')}
                            </Badge>
                          )}
                        </div>
                        {selectedDeployment.createdAt && (
                          <p className="text-muted-foreground mt-2.5 text-[11px]">
                            {t('plugins.details.released', {
                              date: formatTz(selectedDeployment.createdAt, 'MMMM d, yyyy'),
                            })}
                          </p>
                        )}
                      </div>

                      {/* Included Pages */}
                      {selectedDeployment.pages && selectedDeployment.pages.length > 0 && (
                        <div className="bg-muted/20 rounded-b-xl border-t px-4 py-3">
                          <p className="text-muted-foreground mb-2 text-[11px] font-bold uppercase tracking-wide">
                            {t('plugins.details.includedPages')}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedDeployment.pages.map((page, idx) => (
                              <Badge
                                key={`${page.path}-${idx}`}
                                variant="secondary"
                                className="bg-primary/5 text-foreground border-border border px-2 py-0.5 text-[11px] font-medium"
                              >
                                {getPageDisplayName(page.path, page.name)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action buttons for my plugins */}
                      {viewMode === 'mine' && onToggleVisibility && (
                        <div className="flex gap-1 border-t p-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleVisibility(
                                selectedDeployment.deploymentId,
                                !!selectedDeployment.public,
                                !!selectedDeployment.requestedForPublic,
                                e,
                              );
                            }}
                            className="h-7 w-full gap-1 text-xs"
                            title={
                              selectedDeployment.public
                                ? t('plugins.details.publicAdminApproved')
                                : selectedDeployment.requestedForPublic
                                  ? t('plugins.details.cancelPublicRequest')
                                  : t('plugins.details.requestPublicListing')
                            }
                          >
                            {selectedDeployment.public ? (
                              <>
                                <Eye className="h-3 w-3 text-green-600" />
                                <span className="text-green-600">{t('plugins.details.public')}</span>
                              </>
                            ) : selectedDeployment.requestedForPublic ? (
                              <>
                                <Clock className="h-3 w-3 text-amber-600" />
                                <span className="text-amber-600">{t('plugins.details.pending')}</span>
                              </>
                            ) : (
                              <>
                                <EyeOff className="text-muted-foreground h-3 w-3" />
                                <span>{t('plugins.details.requestPublicListing')}</span>
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Config Presets (Prominent - from selected version) */}
            {selectedDeployment?.configPresets &&
              Object.keys(selectedDeployment.configPresets).length > 0 && (
                <div>
                  <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    {t('plugins.details.templateStyles')}
                  </p>
                  <div className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border hover:scrollbar-thumb-muted-foreground/30 max-h-[280px] space-y-2.5 overflow-y-auto px-1 -mx-1">
                    {(() => {
                      const presetKeys = Object.keys(selectedDeployment.configPresets);
                      const defaultPresetId = presetKeys.includes('default') ? 'default' : presetKeys[0];
                      return presetKeys.map((presetId) => {
                        const isActive = selectedPreset === presetId;
                        const isDefault = presetId === defaultPresetId;

                        // Extract meta info from the config object
                        const presetConfig = selectedDeployment.configPresets?.[presetId] as any;
                        const presetName = presetConfig?.meta?.name || presetId;
                        const presetDescription = presetConfig?.meta?.description;
                        const presetInfo = presetConfig?.meta?.info;

                        return (
                          <button
                            key={presetId}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPreset(presetId);
                            }}
                            className={`group relative flex w-full items-center gap-3 overflow-hidden rounded-xl border-2 p-4 text-left transition-all ${
                              isActive
                                ? 'border-primary bg-primary/10 shadow-md'
                                : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50 hover:shadow-sm'
                            }`}
                          >
                            {/* Radio indicator */}
                            <div
                              className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                                isActive
                                  ? 'border-primary bg-primary'
                                  : 'border-muted-foreground/30 bg-background group-hover:border-primary/50'
                              }`}
                            >
                              {isActive && <div className="h-2 w-2 rounded-full bg-white" />}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p
                                  className={`text-sm font-bold leading-tight ${isActive ? 'text-primary' : 'text-foreground'}`}
                                >
                                  {presetName}
                                </p>
                                {presetInfo && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <Info className="text-muted-foreground h-3.5 w-3.5 flex-shrink-0" />
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-[250px]">
                                        {presetInfo}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {isDefault && (
                                  <Badge
                                    variant="secondary"
                                    className="h-5 border border-emerald-200 bg-emerald-100 px-2 text-[10px] font-bold text-emerald-700"
                                  >
                                    {t('plugins.details.recommended')}
                                  </Badge>
                                )}
                              </div>
                              {presetDescription && (
                                <p className="text-muted-foreground mt-1 line-clamp-1 text-xs">
                                  {presetDescription}
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

            {/* Available Versions (Collapsible) */}
            {sortedDeployments.length > 1 && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    {selectedDeployment?.deploymentId === latestDeployment?.deploymentId
                      ? t('plugins.details.otherVersions', { count: sortedDeployments.length - 1 })
                      : t('plugins.details.allVersions', { count: sortedDeployments.length })}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAllVersions(!showAllVersions)}
                    className="h-7 gap-1.5 px-3 text-xs font-medium"
                  >
                    {showAllVersions ? t('plugins.details.hide') : t('plugins.details.showAll')}
                  </Button>
                </div>

                {showAllVersions && (
                  <div className="space-y-2">
                    {sortedDeployments
                      .filter((d) => d.deploymentId !== selectedDeployment?.deploymentId)
                      .map((deployment) => {
                        const isSelected = selectedDeployment?.deploymentId === deployment.deploymentId;
                        const previews = deployment.demoPreviews?.pages?.[0]?.previews || [];
                        const hasPreview = previews.length > 0;
                        const isInstalled = (deployment.instances?.length ?? 0) > 0;
                        const instanceCount = deployment.instances?.length ?? 0;

                        return (
                          <div
                            key={deployment.deploymentId}
                            className={`overflow-hidden rounded-xl transition-all ${
                              isSelected ? 'border-primary border-2 shadow-md' : 'border-border border'
                            }`}
                          >
                            {/* Version Header */}
                            <button
                              onClick={() => setSelectedDeployment(deployment)}
                              className={`w-full p-3 text-left transition-colors ${
                                isSelected ? 'bg-primary/5' : 'hover:bg-muted/50 bg-card'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <code className="font-mono text-sm font-semibold">
                                    v{deployment.version}
                                  </code>
                                  {isInstalled && (
                                    <Badge
                                      variant="outline"
                                      className="h-5 border-emerald-200 bg-emerald-50 text-[10px] font-semibold text-emerald-700"
                                    >
                                      <Check className="mr-0.5 h-2.5 w-2.5" />
                                      {t('plugins.details.installed')}
                                      {instanceCount > 1 && ` (${instanceCount})`}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  {deployment.public ? (
                                    <Badge
                                      variant="outline"
                                      className="h-5 border-green-200 bg-green-50 text-[10px] text-green-700"
                                    >
                                      {t('plugins.details.public')}
                                    </Badge>
                                  ) : deployment.requestedForPublic ? (
                                    <Badge
                                      variant="secondary"
                                      className="h-5 bg-amber-100 text-[10px] text-amber-700"
                                    >
                                      {t('plugins.details.pubReview')}
                                    </Badge>
                                  ) : (
                                    <Badge
                                      variant="secondary"
                                      className="h-5 bg-blue-100 text-[10px] text-blue-700"
                                    >
                                      <Lock className="mr-0.5 h-2.5 w-2.5" />
                                      {t('plugins.details.private')}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {deployment.createdAt && (
                                <p className="text-muted-foreground mt-1 text-[10px]">
                                  <DateDisplay value={deployment.createdAt} mode="date" />
                                </p>
                              )}
                            </button>

                            {/* Included Pages */}
                            {deployment.pages && deployment.pages.length > 0 && (
                              <div className="bg-muted/20 border-t px-3 py-2">
                                <p className="text-muted-foreground mb-1.5 text-[10px] font-bold uppercase tracking-wide">
                                  {t('plugins.details.pagesCount', { count: deployment.pages.length })}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {deployment.pages.map((page, idx) => (
                                    <Badge
                                      key={`${page.path}-${idx}`}
                                      variant="secondary"
                                      className="bg-primary/5 text-foreground px-1.5 py-0.5 text-[10px] font-medium"
                                    >
                                      {getPageDisplayName(page.path, page.name)}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Config Presets count */}
                            {deployment.configPresets && Object.keys(deployment.configPresets).length > 0 && (
                              <div className="bg-muted/20 border-t px-3 py-2">
                                <p className="text-muted-foreground text-center text-[10px]">
                                  {t('plugins.details.presetsAvailable', { count: Object.keys(deployment.configPresets).length })}
                                </p>
                              </div>
                            )}

                            {/* Action buttons for my plugins */}
                            {viewMode === 'mine' && (
                              <div className="bg-muted/10 border-t p-3">
                                {onToggleVisibility && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onToggleVisibility(
                                        deployment.deploymentId,
                                        !!deployment.public,
                                        !!deployment.requestedForPublic,
                                        e,
                                      );
                                    }}
                                    className="h-9 w-full gap-2 text-sm font-medium transition-all hover:shadow-md"
                                    title={
                                      deployment.public
                                        ? t('plugins.details.publicAdminApproved')
                                        : deployment.requestedForPublic
                                          ? t('plugins.details.cancelPublicRequest')
                                          : t('plugins.details.requestPublicListing')
                                    }
                                  >
                                    {deployment.public ? (
                                      <>
                                        <Eye className="h-4 w-4 text-green-600" />
                                        <span className="text-green-600">{t('plugins.details.public')}</span>
                                      </>
                                    ) : deployment.requestedForPublic ? (
                                      <>
                                        <Clock className="h-4 w-4 text-amber-600" />
                                        <span className="text-amber-600">{t('plugins.details.pendingReview')}</span>
                                      </>
                                    ) : (
                                      <>
                                        <EyeOff className="text-muted-foreground h-4 w-4" />
                                        <span>{t('plugins.details.requestPublicListing')}</span>
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="bg-background/95 shrink-0 border-t p-5 backdrop-blur-sm">
            <Button
              onClick={handleInstallClick}
              className="h-14 w-full gap-3 text-base font-bold shadow-lg transition-all hover:shadow-xl"
              size="lg"
              disabled={!displayDeployment || isInstalling}
            >
              {isInstalling ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('plugins.details.installingTemplate')}
                </>
              ) : (
                <>
                  <ShoppingBag className="h-5 w-5" />
                  {readOnly ? t('plugins.details.getThisTemplate') : t('plugins.details.installTemplate')}
                </>
              )}
            </Button>
            {!isInstalling && (
              <p className="text-muted-foreground mt-3 text-center text-xs leading-relaxed">
                {readOnly
                  ? t('plugins.details.signUpToUseTemplate')
                  : t('plugins.details.addToLibrary')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Dialog wrapper for PluginDetails (for backward compatibility)
 */
export function PluginDetailsDialog({ open, onOpenChange, ...props }: PluginDetailsDialogProps) {
  const { t } = useTranslation('storefront');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92dvh] max-h-[92dvh] w-full max-w-[calc(100%-0.5rem)] flex-col gap-0 overflow-hidden p-0 sm:h-[90vh] sm:max-h-[90vh] sm:min-h-[90vh] sm:max-w-[92vw]">
        <VisuallyHidden>
          <DialogTitle>{t('plugins.details.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('plugins.details.dialogDescription')}</DialogDescription>
        </VisuallyHidden>
        <div className="flex-1 overflow-hidden">
          <PluginDetails {...props} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
